/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { Formik, Form, Field } from 'formik'
import _ from 'lodash'
import { useServiceContext } from '../../../root'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { tagsApiService } from '../../../services/api/api.tags'

const persistanceKey = 'tableFilter'

interface FilterFormValues {
  types: Array<{ value: number; label: string }>
  type: string
}

const availableTypes = {
  all: '',
  etmf: 'ETMF',
  nonetmf: 'NON_ETMF',
  rsr: 'REGULATORY_SUBMISSION',
  system: 'SYSTEM'
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    types: [],
    type: availableTypes.all
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(() => {
    const persisted = context.service.persist.getGlobal(persistanceKey) as FilterFormValues | null
    return persisted || getDefaultFilter()
  })

  useEffect(() => {
    const persisted = context.service.persist.getGlobal(persistanceKey) as FilterFormValues | null
    if (persisted && !_.isEqual(persisted, getDefaultFilter())) {
      setFormValues(persisted)
      tableData.applyFilter(persisted)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setGlobal(persistanceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const loadOptions = async (search: string, page: number) => {
    return await tagsApiService.getOptionsForAsyncPicker(search, page)
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-xs-12">
              <Formik initialValues={formValues} onSubmit={handleApplyFilter} enableReinitialize>
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="row">
                      <div className="col-xs-4">
                        <div className="form-group" id="6557ac90-d27e-4661-b080-89c58e13eb31">
                          <label className="control-label">{translate('TAG_NAME')}</label>
                          <InfiniteScrollSelect
                            placeholder={translate('TAG_NAME_MULTISELECT_PLACEHOLDER')}
                            name="types"
                            value={values.types}
                            asyncLoadFn={loadOptions}
                            onChange={(option) => {
                              const types = (option || []) as Array<{ value: number; label: string }>
                              setFieldValue('types', types)
                              setFormValues({ ...values, types })
                            }}
                            isMulti
                            searchable
                            id="6557ac90-d27e-4661-b080-89c58e13eb31"
                          />
                        </div>
                      </div>
                      <div className="col-xs-8">
                        <div className="form-group" id="049b23f3-d69f-48b5-967c-25ec328f4657">
                          <label className="control-label">{translate('TAG_TYPE')}</label>
                          <div>
                            <label className="radio-inline" style={{ fontWeight: 'normal' }}>
                              <Field
                                type="radio"
                                name="type"
                                value={availableTypes.all}
                                checked={values.type === availableTypes.all}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('type', e.target.value)
                                  setFormValues({ ...values, type: e.target.value })
                                }}
                                id={`3080f771-91d3-4df0-b0a0-cac3321f71a5-${availableTypes.all}`}
                              />
                              {translate('ALL')}
                            </label>
                          </div>
                          <div>
                            <label className="radio-inline" style={{ fontWeight: 'normal' }}>
                              <Field
                                type="radio"
                                name="type"
                                value={availableTypes.nonetmf}
                                checked={values.type === availableTypes.nonetmf}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('type', e.target.value)
                                  setFormValues({ ...values, type: e.target.value })
                                }}
                                id={`3080f771-91d3-4df0-b0a0-cac3321f71a5-${availableTypes.nonetmf}`}
                              />
                              {translate('SETTINGS_TAG_TYPE_NON_ETMF')}
                            </label>
                          </div>
                          <div>
                            <label className="radio-inline" style={{ fontWeight: 'normal' }}>
                              <Field
                                type="radio"
                                name="type"
                                value={availableTypes.etmf}
                                checked={values.type === availableTypes.etmf}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('type', e.target.value)
                                  setFormValues({ ...values, type: e.target.value })
                                }}
                                id={`3080f771-91d3-4df0-b0a0-cac3321f71a5-${availableTypes.etmf}`}
                              />
                              {translate('ETMF')}
                            </label>
                          </div>
                          <div>
                            <label className="radio-inline" style={{ fontWeight: 'normal' }}>
                              <Field
                                type="radio"
                                name="type"
                                value={availableTypes.rsr}
                                checked={values.type === availableTypes.rsr}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('type', e.target.value)
                                  setFormValues({ ...values, type: e.target.value })
                                }}
                                id={`3080f771-91d3-4df0-b0a0-cac3321f71a5-${availableTypes.rsr}`}
                              />
                              {translate('REGULATORY_SUBMISSION_RECORD')}
                            </label>
                          </div>
                          <div>
                            <label className="radio-inline" style={{ fontWeight: 'normal' }}>
                              <Field
                                type="radio"
                                name="type"
                                value={availableTypes.system}
                                checked={values.type === availableTypes.system}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('type', e.target.value)
                                  setFormValues({ ...values, type: e.target.value })
                                }}
                                id={`3080f771-91d3-4df0-b0a0-cac3321f71a5-${availableTypes.system}`}
                              />
                              {translate('SETTINGS_TAG_TYPE_SYSTEM')}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleApplyFilter}
                        id="ced6e813-956a-4f81-9404-88fb2eef78a8"
                      >
                        {translate('BTN_FILTER')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={handleClearFilter}
                        disabled={_.isEqual(getDefaultFilter(), values)}
                        id="a6a63f5c-69fa-4214-a824-6c2634c64a72"
                      >
                        {translate('BTN_CLEAR_FILTER')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
